import type { CatalogueStockConfirmation } from "@/lib/catalogue-stock-confirmation";
/**
 * Product Master domain service.
 *
 * The only client-side entry point for product/variant/option/attribute/bundle
 * and media lifecycle operations. Each function maps to a trusted backend RPC
 * (SECURITY DEFINER, domain-gated) so the UI never learns table names, RPC
 * names, or how a multi-table operation is transacted. Business rules (SKU
 * uniqueness, history, archive/delete safety, bundle cycle detection) stay in
 * the backend — this layer only preserves the operation contract.
 */

import { callRpc, callRpcResult } from "@/lib/api/rpc";
import { supabase } from "@/lib/supabase";
import { friendlyError, LocalValidationError } from "@/lib/errors";
import { syncListingDomains } from "@/lib/api/listing-sync";
import { normalizeSku, parseOptionalNumericInput, skuValidationError } from "@/lib/products";
import {
  GOVERNED_MATERIAL_ATTRIBUTE,
  persistableCondition,
  sanitizeManufacturerIdentifier,
} from "@/lib/catalogue-facts";
import {
  planTradeIdentifierWrite,
  type IdentifierRow,
  type TradeIdentifierType,
} from "@/lib/catalogue-identifiers";

export type ProductStatus = "draft" | "active" | "paused" | "archived";
export type VariantStatus = "draft" | "active" | "paused" | "archived";

export interface ReadinessBlock {
  code: string;
  message: string;
  count: number;
}

export interface ArchiveReadiness {
  can_archive: boolean;
  blockers: ReadinessBlock[];
  warnings: ReadinessBlock[];
}

export interface DeleteSafety {
  stock_confirmation?: CatalogueStockConfirmation;
  can_delete: boolean;
  blockers: ReadinessBlock[];
  warnings: ReadinessBlock[];
  requires_stock_disposition: boolean;
  on_hand: number;
  historical_sales: number;
  historical_movements: number;
}

export interface CatalogueDeleteResult {
  deleted: boolean;
  written_off_qty: number;
  cleanup_media_asset_ids: string[];
  historical_sales_preserved: number;
  historical_movements_preserved: number;
}

export interface QualityResult {
  status: string;
  blocking: number;
  warnings: number;
  checks: { level: string; code: string; message: string }[];
}

// ---------------------------------------------------------------------------
// Product lifecycle
// ---------------------------------------------------------------------------

export function setStatus(orgId: string, productId: string, status: ProductStatus): Promise<void> {
  return callRpcResult("set_product_status", {
    p_org_id: orgId,
    p_product_id: productId,
    p_status: status,
  }, "Unable to change this product's status.");
}

export interface SellingStateChannel {
  listing_id: string;
  connector_id: string;
  connector_name: string;
  provider: string | null;
  state: "pending_pause" | "paused" | "pause_failed" | "pending_resume" | "resumed" | "resume_failed";
  last_error: string | null;
}

export interface SellingStateResult {
  success: boolean;
  action: "pause" | "resume";
  product_status: "paused" | "active";
  pause_cycle_id: string | null;
  complete: boolean;
  failures: SellingStateChannel[];
  pending: SellingStateChannel[];
  channels: SellingStateChannel[];
  error?: string;
}

export async function setSellingState(
  orgId: string,
  productId: string,
  action: "pause" | "resume",
): Promise<SellingStateResult> {
  const { data, error } = await supabase.functions.invoke("product-selling-state", {
    body: { org_id: orgId, product_id: productId, action },
  });
  if (error) throw new Error(friendlyError(error.message) || "Unable to update selling state across sales channels.");
  const result = data as SellingStateResult | null;
  if (!result?.success) {
    throw new Error(result?.error || "Unable to update selling state across sales channels.");
  }
  return result;
}


export async function duplicate(
  orgId: string,
  productId: string,
  newName: string,
): Promise<{ productId?: string }> {
  const r = await callRpcResult<{ product_id?: string }>(
    "duplicate_product",
    { p_org_id: orgId, p_product_id: productId, p_new_name: newName },
    "Unable to duplicate this product.",
  );
  return { productId: r.product_id };
}

export function evaluateQuality(orgId: string, productId: string): Promise<QualityResult> {
  return callRpc<QualityResult>("evaluate_product_quality", {
    p_org_id: orgId,
    p_product_id: productId,
  });
}

export function checkArchiveReadiness(orgId: string, productId: string): Promise<ArchiveReadiness> {
  return callRpc<ArchiveReadiness>("check_product_archive_readiness", {
    p_org_id: orgId,
    p_product_id: productId,
  });
}

export function checkDeleteSafety(orgId: string, productId: string): Promise<DeleteSafety> {
  return callRpc<DeleteSafety>("check_product_delete_safety", {
    p_org_id: orgId,
    p_product_id: productId,
  });
}

export function deletePermanently(orgId: string, productId: string, writeOffStock = false, stockConfirmation: CatalogueStockConfirmation | null = null): Promise<CatalogueDeleteResult> {
  return callRpcResult<CatalogueDeleteResult>("delete_product_permanently", {
    p_org_id: orgId,
    p_product_id: productId,
    p_write_off_stock: writeOffStock,
    p_expected_stock: stockConfirmation,
  }, "Unable to delete this product.");
}

export async function retireFromCatalogue(orgId: string, productId: string, confirmLiveListings: boolean) {
  return callRpc<{ ok: boolean; code?: string; live_count?: number; message?: string }>("retire_product_from_catalogue", {
    p_org_id: orgId,
    p_product_id: productId,
    p_confirm_live_listings: confirmLiveListings,
  });
}

// ---------------------------------------------------------------------------
// Product creation + bulk
// ---------------------------------------------------------------------------

export interface InitialStockEntry {
  location_id: string;
  qty: number;
  unit_cost?: number | null;
  /** Variation products only: the variant SKU the entry resolves to. */
  sku?: string;
  /** Simple products only: informational reference label (not persisted to identity). */
  reference?: string;
}

export interface CreateOptionPayload {
  name: string;
  position: number;
  values: { value: string; position: number }[];
}

export interface CreateVariantPayload {
  sku: string;
  barcode?: string | null;
  cost_price?: string | number | null;
  selling_price?: string | number | null;
  weight_kg?: string | number | null;
  length_cm?: string | number | null;
  width_cm?: string | number | null;
  height_cm?: string | number | null;
  position?: number;
  status?: string;
  reorder_point?: number;
  reorder_qty?: number;
  option_values?: Record<string, string>;
}

export interface CreateProductParams {
  orgId: string;
  name: string;
  reference: string | null;
  brand: string | null;
  categoryId: string | null;
  status: "active" | "draft";
  description: string | null;
  shortDescription: string | null;
  seoTitle: string | null;
  metaDescription: string | null;
  tags: string[] | null;
  unit: string;
  productType: "physical" | "bundle";
  variationMode: "simple" | "variation";
  sku: string | null;
  barcode: string | null;
  costPrice: number | null;
  sellingPrice: number | null;
  taxProfile: string | null;
  weightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  reorderPoint: number;
  reorderQty: number;
  options: CreateOptionPayload[];
  variants: CreateVariantPayload[];
  initialStock: InitialStockEntry[] | null;
  idempotencyKey: string;
}

export interface CreatedProduct {
  productId?: string;
  variantCount?: number;
  sku?: string | null;
}

export async function createProduct(params: CreateProductParams): Promise<CreatedProduct> {
  const canonicalSku = params.sku ? normalizeSku(params.sku) : null;
  if (canonicalSku) {
    const skuError = skuValidationError(canonicalSku);
    // Validated before the RPC: nothing was sent, so the outcome is known.
    if (skuError) throw new LocalValidationError(skuError);
  }
  const canonicalVariants = params.variants?.map((variant) => {
    const sku = normalizeSku(variant.sku);
    const skuError = skuValidationError(sku);
    if (skuError) throw new LocalValidationError(`${skuError} Variant: ${variant.sku || "unnamed"}.`);
    return { ...variant, sku };
  });
  const canonicalInitialStock = params.initialStock?.map((entry) => (
    entry.sku ? { ...entry, sku: normalizeSku(entry.sku) } : entry
  ));

  // Fail early on malformed opening stock rather than letting the backend
  // silently skip entries (which would corrupt a multi-location count).
  if (params.initialStock) {
    for (const entry of params.initialStock) {
      if (!entry.location_id || entry.location_id.trim() === "") {
        throw new LocalValidationError("Opening stock is missing a valid location.");
      }
      if (!Number.isFinite(entry.qty) || entry.qty <= 0) {
        throw new LocalValidationError("Opening stock quantity must be a positive number.");
      }
    }
  }

  const result = await callRpc<{
    product_id?: string;
    variant_count?: number;
    sku?: string | null;
  }>("create_product", {
    p_org_id: params.orgId,
    p_name: params.name,
    p_reference: params.reference,
    p_brand: params.brand,
    p_category_id: params.categoryId,
    p_status: params.status,
    p_description: params.description,
    p_short_description: params.shortDescription,
    p_seo_title: params.seoTitle,
    p_meta_description: params.metaDescription,
    p_tags: params.tags,
    p_unit: params.unit,
    p_product_type: params.productType,
    p_variation_mode: params.variationMode,
    p_sku: canonicalSku,
    p_barcode: params.barcode,
    p_cost_price: params.costPrice,
    p_selling_price: params.sellingPrice,
    p_tax_profile: params.taxProfile,
    p_weight_kg: params.weightKg,
    p_length_cm: params.lengthCm,
    p_width_cm: params.widthCm,
    p_height_cm: params.heightCm,
    p_reorder_point: params.reorderPoint,
    p_reorder_qty: params.reorderQty,
    p_options: params.options,
    p_variants: canonicalVariants ?? params.variants,
    p_initial_stock: canonicalInitialStock ?? params.initialStock,
    p_idempotency_key: params.idempotencyKey,
  });

  return {
    productId: result.product_id,
    variantCount: result.variant_count,
    sku: result.sku ?? null,
  };
}

export interface BulkUpdateResult {
  succeeded: number;
  failed: { id: string; reason: string }[];
  changes?: { id: string; old: number | null; new: number | null }[];
}

export function bulkUpdate(
  orgId: string,
  ids: string[],
  action: string,
  params: Record<string, unknown> = {},
): Promise<BulkUpdateResult> {
  return callRpc<BulkUpdateResult>("bulk_update_products_v2", {
    p_org_id: orgId,
    p_ids: ids,
    p_action: action,
    p_params: params,
  });
}

export function getCategoryProductCounts(orgId: string): Promise<{ category_id: string; cnt: number }[]> {
  return callRpc<{ category_id: string; cnt: number }[]>("get_category_product_counts", {
    p_org_id: orgId,
  });
}

// ---------------------------------------------------------------------------
// Variants / options
// ---------------------------------------------------------------------------

export interface VariantMatrixOption {
  name: string;
  position: number;
  values: { value: string; position: number }[];
}

export function createVariantMatrix(
  orgId: string,
  productId: string,
  options: VariantMatrixOption[],
  variants: Record<string, unknown>[],
  confirmRetiredCombinations = false,
): Promise<void> {
  return callRpc("create_variant_matrix", {
    p_org_id: orgId,
    p_product_id: productId,
    p_options: options,
    p_variants: variants,
    p_confirm_retired_combinations: confirmRetiredCombinations,
  });
}

export function updateVariantOptions(
  orgId: string,
  variantId: string,
  optionValues: Record<string, string>,
): Promise<void> {
  return callRpc("update_variant_options", {
    p_org_id: orgId,
    p_variant_id: variantId,
    p_option_values: optionValues,
  });
}

export function setVariantStatus(orgId: string, variantId: string, status: VariantStatus): Promise<void> {
  return callRpcResult("set_variant_status", {
    p_org_id: orgId,
    p_variant_id: variantId,
    p_status: status,
  }, "Unable to change this variant's status.");
}

export function checkVariantArchiveReadiness(orgId: string, variantId: string): Promise<ArchiveReadiness> {
  return callRpc<ArchiveReadiness>("check_variant_archive_readiness", {
    p_org_id: orgId,
    p_variant_id: variantId,
  });
}

export function checkVariantDeleteSafety(orgId: string, variantId: string): Promise<DeleteSafety> {
  return callRpc<DeleteSafety>("check_variant_delete_safety", {
    p_org_id: orgId,
    p_variant_id: variantId,
  });
}

export function deleteVariantPermanently(orgId: string, variantId: string, writeOffStock = false, stockConfirmation: CatalogueStockConfirmation | null = null): Promise<CatalogueDeleteResult> {
  return callRpcResult<CatalogueDeleteResult>("delete_variant_permanently", {
    p_org_id: orgId,
    p_variant_id: variantId,
    p_write_off_stock: writeOffStock,
    p_expected_stock: stockConfirmation,
  }, "Unable to delete this variant.");
}

export function backfillDefaultVariants(orgId: string): Promise<void> {
  return callRpc("backfill_default_variants", { p_org_id: orgId });
}

// ---------------------------------------------------------------------------
// Bundles
// ---------------------------------------------------------------------------

export interface BundleAvailabilityLeaf {
  component_variant_id: string;
  sku: string | null;
  product_name: string | null;
  qty_per_unit: number;
  available: number;
  units_possible: number;
}

export interface BundleAvailability {
  sellable_quantity: number;
  limiting_component_variant_id: string | null;
  components: BundleAvailabilityLeaf[];
}

export function getBundleAvailability(
  orgId: string,
  parentVariantId: string,
): Promise<BundleAvailability> {
  return callRpc<BundleAvailability>("get_bundle_availability_detail", {
    p_org_id: orgId,
    p_parent_variant_id: parentVariantId,
  });
}

export function setBundleComponents(
  orgId: string,
  parentVariantId: string,
  components: { component_variant_id: string; qty: number }[],
): Promise<void> {
  return callRpc("set_bundle_components", {
    p_org_id: orgId,
    p_parent_variant_id: parentVariantId,
    p_components: components,
  });
}

// ---------------------------------------------------------------------------
// Attributes
// ---------------------------------------------------------------------------

export interface UpsertAttributeParams {
  orgId: string;
  name: string;
  dataType: string;
  options: string[];
  required: boolean;
  units: string | null;
  categoryId: string | null;
  position: number;
}

export async function upsertAttribute(params: UpsertAttributeParams): Promise<string | null> {
  const data = await callRpc<{ attribute_id?: string }>("upsert_product_attribute", {
    p_org_id: params.orgId,
    p_name: params.name,
    p_data_type: params.dataType,
    p_options: params.options,
    p_required: params.required,
    p_units: params.units,
    p_category_id: params.categoryId,
    p_position: params.position,
  });
  return data?.attribute_id ?? null;
}

export function deleteAttribute(orgId: string, attributeId: string): Promise<void> {
  return callRpc("delete_product_attribute", {
    p_org_id: orgId,
    p_attribute_id: attributeId,
  });
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export function setPrimaryMedia(orgId: string, productId: string, productMediaId: string): Promise<void> {
  return callRpc("set_product_primary_media", {
    p_org_id: orgId,
    p_product_id: productId,
    p_product_media_id: productMediaId,
  });
}

export function reorderProductMedia(
  orgId: string,
  productId: string,
  orderedIds: string[],
): Promise<void> {
  return callRpc("reorder_product_media", {
    p_org_id: orgId,
    p_product_id: productId,
    p_ordered_ids: orderedIds,
  });
}

export function archiveMediaAsset(orgId: string, mediaAssetId: string): Promise<void> {
  return callRpcResult("archive_media_asset", {
    p_org_id: orgId,
    p_media_asset_id: mediaAssetId,
  }, "Unable to delete this media asset.");
}

// ---------------------------------------------------------------------------
// RLS-guarded single-row edits
//
// These update plain scalar columns on the caller's own org-scoped row, which
// RLS already scopes. They are intentionally not routed through a trusted RPC
// because there is no cross-table invariant to enforce at write time: SKU
// uniqueness is a DB constraint, and history/audit are captured by triggers.
// The `update_product` / `update_variant` RPCs recorded in the architectural
// debt register will supersede these once they exist.
// ---------------------------------------------------------------------------

export interface ProductEditForm {
  name: string;
  reference: string;
  barcode: string;
  category_id: string;
  description: string;
  short_description: string;
  seo_title: string;
  meta_description: string;
  seo_keywords?: string;
  tags: string;
  brand: string;
  cost_price: string;
  selling_price: string;
  reorder_point: string;
  reorder_qty: string;
  status: string;
  weight_kg: string;
  length_cm: string;
  width_cm: string;
  height_cm: string;
  manufacturer: string;
  model_number: string;
  mpn: string;
  country_of_origin: string;
  hs_tariff_code: string;
  customs_description: string;
  unit_of_measure: string;
  tax_profile: string;
  shipping_profile: string;
  condition: string;
}

/**
 * Persist a full product edit. Does not publish to marketplaces.
 * Coerces the flat string form into canonical column types, keeps the (single)
 * variant's barcode in sync for simple products, and throws a friendly error on
 * failure. Product selling_price is the inherited base; variant overrides stay
 * on variants.selling_price.
 */
export async function updateProduct(
  orgId: string,
  productId: string,
  form: ProductEditForm,
  singleVariantId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("products")
    .update({
      name: form.name,
      reference: form.reference || null,
      barcode: form.barcode || null,
      category_id: form.category_id || null,
      description: form.description || null,
      short_description: form.short_description || null,
      seo_title: form.seo_title || null,
      meta_description: form.meta_description || null,
      seo_keywords: form.seo_keywords || null,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : null,
      brand: form.brand || null,
      cost_price: parseOptionalNumericInput(form.cost_price),
      selling_price: parseOptionalNumericInput(form.selling_price),
      reorder_point: Number(form.reorder_point) || 0,
      reorder_qty: Number(form.reorder_qty) || 0,
      status: form.status,
      weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      length_cm: form.length_cm ? Number(form.length_cm) : null,
      width_cm: form.width_cm ? Number(form.width_cm) : null,
      height_cm: form.height_cm ? Number(form.height_cm) : null,
      manufacturer: sanitizeManufacturerIdentifier(form.manufacturer),
      model_number: sanitizeManufacturerIdentifier(form.model_number),
      mpn: sanitizeManufacturerIdentifier(form.mpn),
      country_of_origin: sanitizeManufacturerIdentifier(form.country_of_origin),
      hs_tariff_code: form.hs_tariff_code || null,
      customs_description: form.customs_description || null,
      unit_of_measure: form.unit_of_measure || null,
      tax_profile: form.tax_profile || null,
      shipping_profile: form.shipping_profile || null,
      condition: persistableCondition(form.condition),
    })
    .eq("id", productId)
    .eq("org_id", orgId);
  if (error) throw new Error(friendlyError(error.message));

  // Barcode is authoritative on the variant (scans resolve against it), so a
  // simple product's variant barcode stays in sync with this edit.
  if (singleVariantId) {
    const { error: variantErr } = await supabase
      .from("variants")
      .update({ barcode: form.barcode.trim() || null })
      .eq("id", singleVariantId)
      .eq("org_id", orgId);
    if (variantErr) throw new Error(friendlyError(variantErr.message));
  }
  await syncListingDomains({ orgId, productId, domains: ["pricing"] }).catch(() => ({ success: false }));
}

/** Catalogue-only: set the product base. Inherited copies of the new base become NULL via trigger. */
export async function updateProductSellingPrice(
  orgId: string,
  productId: string,
  sellingPrice: number,
): Promise<void> {
  const { error } = await supabase
    .from("products")
    .update({ selling_price: sellingPrice })
    .eq("id", productId)
    .eq("org_id", orgId);
  if (error) throw new Error(friendlyError(error.message));
  await syncListingDomains({ orgId, productId, domains: ["pricing"] }).catch(() => ({ success: false }));
}

export async function applyCanonicalProductData(
  orgId: string,
  productId: string,
  data: {
    manufacturer?: string | null;
    model_number?: string | null;
    mpn?: string | null;
    condition?: string | null;
    country_of_origin?: string | null;
    unit_of_measure?: string | null;
    seo_keywords?: string | null;
    url_slug?: string | null;
    identifiers_not_applicable?: boolean;
    gtin?: string | null;
    upc?: string | null;
    ean?: string | null;
    material?: string | null;
  },
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (data.manufacturer !== undefined) patch.manufacturer = sanitizeManufacturerIdentifier(data.manufacturer);
  if (data.model_number !== undefined) patch.model_number = sanitizeManufacturerIdentifier(data.model_number);
  if (data.mpn !== undefined) patch.mpn = sanitizeManufacturerIdentifier(data.mpn);
  if (data.condition !== undefined) patch.condition = persistableCondition(data.condition);
  if (data.country_of_origin !== undefined) patch.country_of_origin = sanitizeManufacturerIdentifier(data.country_of_origin);
  if (data.unit_of_measure !== undefined) patch.unit_of_measure = data.unit_of_measure || null;
  if (data.seo_keywords !== undefined) patch.seo_keywords = data.seo_keywords || null;
  if (data.url_slug !== undefined) patch.url_slug = data.url_slug || null;
  if (data.identifiers_not_applicable !== undefined) patch.identifiers_not_applicable = Boolean(data.identifiers_not_applicable);
  if (Object.keys(patch).length > 0) {
    const { error } = await supabase
      .from("products")
      .update(patch)
      .eq("id", productId)
      .eq("org_id", orgId);
    if (error) throw new Error(friendlyError(error.message));
  }

  if (data.material !== undefined) {
    await persistGovernedTextAttribute(orgId, productId, GOVERNED_MATERIAL_ATTRIBUTE, data.material);
  }

  if (data.identifiers_not_applicable) return;
  await upsertCanonicalTradeIdentifiers(orgId, productId, null, {
    gtin: data.gtin,
    upc: data.upc,
    ean: data.ean,
    identifiersNotApplicable: false,
  });
}

export async function upsertCanonicalTradeIdentifiers(
  orgId: string,
  productId: string,
  variantId: string | null,
  data: {
    gtin?: string | null;
    upc?: string | null;
    ean?: string | null;
    identifiersNotApplicable?: boolean;
  },
): Promise<void> {
  const types: TradeIdentifierType[] = ["gtin", "upc", "ean"];
  for (const type of types) {
    const raw = data[type];
    if (raw === undefined && !data.identifiersNotApplicable) continue;
    await upsertCanonicalTradeIdentifier({
      orgId,
      productId,
      variantId,
      type,
      rawValue: raw ?? null,
      identifiersNotApplicable: Boolean(data.identifiersNotApplicable),
    });
  }
}

export async function upsertCanonicalTradeIdentifier(params: {
  orgId: string;
  productId: string;
  variantId: string | null;
  type: TradeIdentifierType;
  rawValue: string | null;
  identifiersNotApplicable?: boolean;
}): Promise<{ action: string }> {
  const { data: orgRows, error: lookErr } = await supabase
    .from("product_identifiers")
    .select("id, org_id, product_id, variant_id, type, value")
    .eq("org_id", params.orgId)
    .eq("type", params.type);
  if (lookErr) throw new Error(friendlyError(lookErr.message));
  const plan = planTradeIdentifierWrite({
    type: params.type,
    rawValue: params.rawValue,
    identifiersNotApplicable: Boolean(params.identifiersNotApplicable),
    orgId: params.orgId,
    productId: params.productId,
    variantId: params.variantId,
    orgRowsOfType: (orgRows ?? []) as IdentifierRow[],
  });
  if (plan.action === "reject") throw new Error(plan.error);
  if (plan.action === "insert") {
    const { error } = await supabase.from("product_identifiers").insert({
      org_id: params.orgId,
      product_id: params.productId,
      variant_id: params.variantId,
      type: params.type,
      value: plan.value,
    });
    if (error) throw new Error(friendlyError(error.message));
  }
  if (plan.action === "update") {
    const { error } = await supabase
      .from("product_identifiers")
      .update({ value: plan.value })
      .eq("id", plan.id)
      .eq("org_id", params.orgId);
    if (error) throw new Error(friendlyError(error.message));
  }
  return { action: plan.action };
}

export async function persistGovernedTextAttribute(
  orgId: string,
  productId: string,
  name: string,
  value: string | null | undefined,
): Promise<void> {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    const existing = await supabase
      .from("product_attributes")
      .select("id")
      .eq("org_id", orgId)
      .eq("name", name)
      .maybeSingle();
    if (existing.error) throw new Error(friendlyError(existing.error.message));
    if (!existing.data?.id) return;
    const { error: delErr } = await supabase
      .from("product_attribute_values")
      .delete()
      .eq("org_id", orgId)
      .eq("product_id", productId)
      .eq("attribute_id", existing.data.id);
    if (delErr) throw new Error(friendlyError(delErr.message));
    return;
  }
  const attrId = await upsertAttribute({
    orgId,
    name,
    dataType: "text",
    options: [],
    required: false,
    units: null,
    categoryId: null,
    position: 0,
  });
  if (!attrId) throw new Error(`Unable to save ${name}.`);
  const { error: upsertErr } = await supabase.from("product_attribute_values").upsert(
    { org_id: orgId, product_id: productId, attribute_id: attrId, value: trimmed },
    { onConflict: "org_id,product_id,attribute_id" },
  );
  if (upsertErr) throw new Error(friendlyError(upsertErr.message));
}

export async function setChannelAllocation(
  orgId: string,
  productId: string,
  connectorId: string,
  channelQty: number,
  safetyStock: number,
): Promise<void> {
  const { error } = await supabase.from("channel_allocations").upsert(
    {
      org_id: orgId,
      product_id: productId,
      connector_id: connectorId,
      channel_qty: channelQty,
      safety_stock: safetyStock,
      reserved_qty: 0,
    },
    { onConflict: "org_id,product_id,connector_id" },
  );
  if (error) throw new Error(friendlyError(error.message));
}

export function saveProductVariant(params: {
  orgId: string;
  productId: string;
  variantId: string | null;
  sku: string;
  barcode: string | null;
  cost_price: number | null;
  selling_price: number | null;
  weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  optionValues: Record<string, string>;
  position: number;
  confirmRetiredCombination?: boolean;
}): Promise<{ success: boolean; variant_id: string; sku?: string }> {
  const sku = normalizeSku(params.sku);
  const skuError = skuValidationError(sku);
  if (skuError) return Promise.reject(new Error(skuError));
  return callRpcResult("save_product_variant", {
    p_org_id: params.orgId,
    p_product_id: params.productId,
    p_variant_id: params.variantId,
    p_sku: sku,
    p_barcode: params.barcode,
    p_cost_price: params.cost_price,
    p_selling_price: params.selling_price,
    p_weight_kg: params.weight_kg,
    p_length_cm: params.length_cm,
    p_width_cm: params.width_cm,
    p_height_cm: params.height_cm,
    p_option_values: params.optionValues,
    p_position: params.position,
    p_confirm_retired_combination: params.confirmRetiredCombination === true,
  }, "Unable to save this variant.");
}

export interface VariantSkuStandardizationChange {
  variant_id: string;
  expected_old_sku: string;
  new_sku: string;
}

export interface VariantSkuStandardizationResult {
  success: boolean;
  updated?: number;
  code?: string;
  variant_id?: string;
  sku?: string;
  blockers?: Array<{ code: string; message: string; count?: number }>;
  changes?: Array<{ variant_id: string; old_sku: string; new_sku: string }>;
}

export function standardizeProductVariantSkus(
  orgId: string,
  productId: string,
  baseSku: string,
  changes: VariantSkuStandardizationChange[],
): Promise<VariantSkuStandardizationResult> {
  const canonicalBase = normalizeSku(baseSku);
  const baseError = skuValidationError(canonicalBase);
  if (baseError) return Promise.reject(new Error(baseError));
  for (const change of changes) {
    const skuError = skuValidationError(change.new_sku);
    if (skuError) return Promise.reject(new Error(`${skuError} Proposed SKU: ${change.new_sku}.`));
  }
  return callRpc<VariantSkuStandardizationResult>("standardize_product_variant_skus", {
    p_org_id: orgId,
    p_product_id: productId,
    p_base_sku: canonicalBase,
    p_changes: changes,
  });
}

export async function saveVariantSellingPrices(
  orgId: string,
  productId: string,
  lines: { variant_id: string; selling_price: number | null }[],
): Promise<{ success: boolean; updated: number }> {
  const saved = await callRpcResult<{ success: boolean; updated: number }>("save_variant_selling_prices", {
    p_org_id: orgId,
    p_product_id: productId,
    p_lines: lines,
  }, "Unable to save variant prices.");
  await syncListingDomains({ orgId, productId, domains: ["pricing"] }).catch(() => ({ success: false }));
  return saved;
}